import { useEffect, lazy, Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Layout from "@/components/layout";
import Home from "@/pages/home";

const AdminChat = lazy(() => import("@/pages/admin-chat"));
const AdminBlog = lazy(() => import("@/pages/admin-blog"));
const FAQ = lazy(() => import("@/pages/faq"));
const About = lazy(() => import("@/pages/about"));
const Services = lazy(() => import("@/pages/services"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Terms = lazy(() => import("@/pages/terms"));
const Insights = lazy(() => import("@/pages/insights"));
const SuccessStories = lazy(() => import("@/pages/success-stories"));
const Approach = lazy(() => import("@/pages/approach"));
const Team = lazy(() => import("@/pages/team"));
const Products = lazy(() => import("@/pages/products"));
const ContactPage = lazy(() => import("@/pages/contact"));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPostPage = lazy(() => import("@/pages/blog-post"));
const Locations = lazy(() => import("@/pages/locations"));
const LocationDetail = lazy(() => import("@/pages/location-detail"));
const ServiceDetail = lazy(() => import("@/pages/service-detail"));
const WhySLA = lazy(() => import("@/pages/why-sla"));
const NotFound = lazy(() => import("@/pages/not-found"));

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

function Router() {
  return (
    <>
    <ScrollToTop />
    <Switch>
      <Route path="/admin/chat" component={AdminChat} />
      <Route path="/admin/blog" component={AdminBlog} />
      <Route path="/">
        <Layout>
          <Home />
        </Layout>
      </Route>
      <Route path="/blog/:slug">
        <Layout>
          <BlogPostPage />
        </Layout>
      </Route>
      <Route path="/blog">
        <Layout>
          <Blog />
        </Layout>
      </Route>
      <Route path="/insights">
        <Layout>
          <Insights />
        </Layout>
      </Route>
      <Route path="/faq">
        <Layout>
          <FAQ />
        </Layout>
      </Route>
      <Route path="/about">
        <Layout>
          <About />
        </Layout>
      </Route>
      <Route path="/services">
        <Layout>
          <Services />
        </Layout>
      </Route>
      <Route path="/success-stories">
        <Layout>
          <SuccessStories />
        </Layout>
      </Route>
      <Route path="/why-sla">
        <Layout>
          <WhySLA />
        </Layout>
      </Route>
      <Route path="/approach">
        <Layout>
          <Approach />
        </Layout>
      </Route>
      <Route path="/team">
        <Layout>
          <Team />
        </Layout>
      </Route>
      <Route path="/services/:slug">
        <Layout>
          <ServiceDetail />
        </Layout>
      </Route>
      <Route path="/products">
        <Layout>
          <Products />
        </Layout>
      </Route>
      <Route path="/locations/:slug">
        <Layout>
          <LocationDetail />
        </Layout>
      </Route>
      <Route path="/locations">
        <Layout>
          <Locations />
        </Layout>
      </Route>
      <Route path="/contact">
        <Layout>
          <ContactPage />
        </Layout>
      </Route>
      <Route path="/privacy">
        <Layout>
          <Privacy />
        </Layout>
      </Route>
      <Route path="/terms">
        <Layout>
          <Terms />
        </Layout>
      </Route>
      <Route>
        <Layout>
          <NotFound />
        </Layout>
      </Route>
    </Switch>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="flex flex-col items-center gap-4">
              <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        }>
          <Router />
        </Suspense>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
